import Link from 'next/link';
import Image from 'next/image';
import Price from '@/components/price/Price';
import { getLowestBundlePriceSummary } from '@/lib/bundles/utils';
import { resolveImageAltText } from '@/lib/utils';
import { useModal } from '@/components/modal/ModalProvider';

export default function BundleCard({
  cardData,
  index,
  buttonText,
  collectionPage
}) {
  const { showPriceDetails } = useModal();
  const {
    card_title,
    description,
    expanded_card,
    superlative,
    superlative_feature_styling,
    card_type,
    extendedData
  } = cardData;
  const bundleTypeData = card_type?.[0]?.bundle;
  const { bundle, products } = extendedData || {};
  const { image, discount_message } = bundleTypeData || {};

  if (!propsAreValid(bundle, products, image)) {
    console.warn('[BundleCard] Missing required data', card_title);
    return null;
  }

  const { totalPrice: bundlePrice, totalComparePrice: bundleCompareAtPrice } =
    getLowestBundlePriceSummary(
      bundle.pricingMetafield,
      products.map((p) => p.shopifyProduct)
    );

  function handlePriceModalClick(e, title) {
    e.preventDefault();
    showPriceDetails(`bundle-card-${title}`, null, null);
  }
  const cardIndex = index;

  return (
    <div
      id={`product-group-card-${cardData?.uid}`}
      className={`border-dawn-grey-30 border bg-white ${expanded_card ? 'lg:col-span-3' : 'col-span-1'} flex`}
      data-optimizely="product-group-card"
    >
      {expanded_card && (
        <Link href={bundle.url} prefetch={false}>
          <div className="max-h-[375px] overflow-hidden">
            <Image
              src={image.url}
              alt={resolveImageAltText(image)}
              width="765"
              height="372"
              sizes="765px"
              priority={collectionPage && cardIndex <= 2 ? true : false}
              className="hidden lg:block"
            />
          </div>
        </Link>
      )}
      <div className="flex flex-col justify-between">
        <div
          className={`flex flex-col px-6 pt-5 ${expanded_card ? 'lg:h-full lg:justify-center lg:px-10' : ''}`}
        >
          <Link
            href={bundle.url}
            prefetch={false}
            title={bundle.title}
            className="flex flex-wrap"
            data-cy={bundle.title}
          >
            <h3 className="h3 nowrap mr-3">{card_title}</h3>
            {superlative && (
              <div
                className={`${superlative_feature_styling ? 'bg-fiery-cognac-110 text-cozy-cognac-10' : 'bg-warm-pewter-80 text-burnt-cognac-130'} badge-text-r nowrap self-center rounded-full px-3 py-[2px] text-center`}
              >
                {superlative}
              </div>
            )}
          </Link>
          {description && <p className="body-s pt-2">{description}</p>}
          <div className="flex pt-3 pb-5">
            <div className="price-r-label mr-2">From:</div>
            <div>
              <Price
                price={bundlePrice}
                compareAtPrice={bundleCompareAtPrice}
                priceClassOverride="price-r text-burnt-cognac"
              />
              <button
                className="price-s-label text-grey-text block cursor-pointer underline"
                onClick={(e) => handlePriceModalClick(e, bundle.title)}
              >
                Price Details
              </button>
            </div>
            {discount_message && (
              <div className="badge-text-r border-burnt-cognac text-burnt-cognac mt-[2px] self-start rounded-full border px-2 py-px text-center">
                {discount_message}
              </div>
            )}
          </div>
        </div>
        <div>
          <Link
            href={bundle.url}
            prefetch={false}
            title={bundle.title}
            aria-label={`${buttonText} for ${bundle.title}`}
            data-optimizely="product-group-card-cta"
          >
            <div className="max-h-[310px] overflow-hidden">
              <Image
                src={image.url}
                alt={resolveImageAltText(image)}
                width="598"
                height="310"
                sizes="(min-width: 1536px) 372px, (min-width: 1280px) 345px, (min-width: 1024px) 260px, (min-width: 768px) 323px, (min-width: 640px) 260px, 598px"
                priority={collectionPage && cardIndex <= 2 ? true : false}
                className={expanded_card ? 'block lg:hidden' : ''}
              />
            </div>
            <div className="btn btn-primary w-full">{buttonText}</div>
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Simple validation
 */
function propsAreValid(bundle, products, image) {
  if (!image?.url) {
    return false;
  }

  if (!bundle?.pricingMetafield || !bundle.url || !bundle.title) {
    return false;
  }

  if (!Array.isArray(products)) {
    return false;
  }

  for (const product of products) {
    if (!product?.shopifyProduct) {
      return false;
    }
  }

  return true;
}
